import React, { useMemo, useState } from "react";
import { ReloadOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Card, DatePicker, message, Select, Tabs } from "antd";
import { type RangePickerProps } from "antd/es/date-picker";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { PermissionKey, PermissionScope } from "@fitness-app/data-models";

import { usePermission } from "~/hooks/usePermission";
import { analyticsApi } from "~/store/initializeStore";

import { CheckoutFunnelTab } from "./checkoutFunnel/CheckoutFunnelTab";
import { CampaignsTable } from "./components/CampaignsTable";
import { LeadsTable } from "./components/LeadsTable";
import { useMarketingCampaigns } from "./hooks/useMarketingCampaigns";
import { useMarketingFilters } from "./hooks/useMarketingFilters";
import { useMarketingLeads } from "./hooks/useMarketingLeads";
import {
  formatMarketingDate,
  isMarketingDateSpanWithinLimit,
  MARKETING_DATE_FORMAT,
  MAX_MARKETING_DATE_RANGE_DAYS,
} from "./marketingFilters";

const { RangePicker } = DatePicker;

const TABS = ["campaigns", "leads", "checkout"] as const;
type MarketingTab = (typeof TABS)[number];

const isDateOutsideMaximumRange: RangePickerProps["disabledDate"] = (date, { from }) =>
  Boolean(from && !isMarketingDateSpanWithinLimit(formatMarketingDate(from), formatMarketingDate(date)));

const MarketingAnalytics = (): React.ReactElement => {
  const { t } = useTranslation("dashboard");
  const queryClient = useQueryClient();
  const canManageMarketingJobs = usePermission(PermissionKey.TEAM_PERMISSIONS_MANAGE, PermissionScope.ALL);
  const { filters, dateRange, setDateRange, setCampaign, setRep } = useMarketingFilters();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const activeTab: MarketingTab = TABS.includes(requestedTab as MarketingTab)
    ? (requestedTab as MarketingTab)
    : "campaigns";
  const isCheckoutTab = activeTab === "checkout";
  const [syncing, setSyncing] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const {
    campaigns,
    error: campaignsError,
    isLoading: campaignsLoading,
    isFetching: campaignsFetching,
  } = useMarketingCampaigns(filters);
  const { leads, error: leadsError, isLoading: leadsLoading, isFetching: leadsFetching } = useMarketingLeads(filters);

  const campaignOptions = useMemo(() => {
    const uniqueCampaigns = new Map<string, string>();

    campaigns.forEach((campaign) => {
      if (campaign.campaignId) {
        uniqueCampaigns.set(campaign.campaignId, campaign.campaignName);
      }
    });

    return Array.from(uniqueCampaigns, ([value, label]) => ({ value, label }));
  }, [campaigns]);

  const repOptions = useMemo(() => {
    const reps = new Set<string>();
    leads.forEach((lead) => lead.assignedTo && reps.add(lead.assignedTo));

    return Array.from(reps)
      .sort((firstRep, secondRep) => firstRep.localeCompare(secondRep, "pl"))
      .map((rep) => ({ value: rep, label: rep }));
  }, [leads]);

  const handleSyncMeta = async () => {
    setSyncing(true);

    try {
      await analyticsApi.post("/marketing/sync-meta-insights", {});
      await queryClient.invalidateQueries({ queryKey: ["marketingCampaigns"] });
      void message.success(t("marketingAnalytics.sync.success"));
    } catch {
      void message.error(t("marketingAnalytics.sync.error"));
    } finally {
      setSyncing(false);
    }
  };

  const handleRebuildLeads = async () => {
    setRebuilding(true);

    try {
      await analyticsApi.post("/marketing/build-leads", {});
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["marketingLeads"] }),
        queryClient.invalidateQueries({ queryKey: ["marketingCampaigns"] }),
      ]);
      void message.success(t("marketingAnalytics.rebuild.success"));
    } catch {
      void message.error(t("marketingAnalytics.rebuild.error"));
    } finally {
      setRebuilding(false);
    }
  };

  const handleDateRangeChange: RangePickerProps["onChange"] = (value) => {
    if (!value?.[0] || !value[1]) {
      return;
    }

    const from = formatMarketingDate(value[0]);
    const to = formatMarketingDate(value[1]);

    if (!isMarketingDateSpanWithinLimit(from, to)) {
      void message.error(
        t("marketingAnalytics.filters.rangeTooLong", {
          days: MAX_MARKETING_DATE_RANGE_DAYS,
        }),
      );
      return;
    }

    setDateRange([value[0], value[1]]);
  };

  return (
    <Card title={t("marketingAnalytics.title")}>
      <div className="mb-4 flex flex-wrap gap-3">
        <RangePicker
          aria-label={t("marketingAnalytics.filters.dateRange")}
          className="w-full sm:w-auto"
          value={dateRange}
          allowClear={false}
          disabledDate={isDateOutsideMaximumRange}
          format={MARKETING_DATE_FORMAT}
          onChange={handleDateRangeChange}
        />
        {isCheckoutTab ? null : (
          <>
            <Select
              aria-label={t("marketingAnalytics.filters.campaign")}
              allowClear
              showSearch
              className="w-full sm:w-52"
              placeholder={t("marketingAnalytics.filters.campaign")}
              options={campaignOptions}
              value={filters.campaign}
              optionFilterProp="label"
              onChange={(value) => setCampaign(value ?? null)}
            />
            <Select
              aria-label={t("marketingAnalytics.filters.rep")}
              allowClear
              showSearch
              className="w-full sm:w-48"
              placeholder={t("marketingAnalytics.filters.rep")}
              options={repOptions}
              value={filters.rep}
              optionFilterProp="label"
              onChange={(value) => setRep(value ?? null)}
            />
          </>
        )}
        {canManageMarketingJobs && !isCheckoutTab ? (
          <>
            <Button
              className="w-full sm:w-auto"
              htmlType="button"
              icon={<ReloadOutlined />}
              loading={syncing}
              onClick={handleSyncMeta}
            >
              {t("marketingAnalytics.sync.button")}
            </Button>
            <Button
              className="w-full sm:w-auto"
              htmlType="button"
              icon={<ReloadOutlined />}
              loading={rebuilding}
              onClick={handleRebuildLeads}
            >
              {t("marketingAnalytics.rebuild.button")}
            </Button>
          </>
        ) : null}
      </div>
      {!isCheckoutTab && (campaignsError || leadsError) ? (
        <Alert className="mb-4" type="error" showIcon message={t("marketingAnalytics.loadError")} />
      ) : null}
      <Tabs
        activeKey={activeTab}
        onChange={(tab) =>
          setSearchParams((current) => {
            const next = new URLSearchParams(current);
            if (tab === "campaigns") next.delete("tab");
            else next.set("tab", tab);
            return next;
          })
        }
        items={[
          {
            key: "campaigns",
            label: t("marketingAnalytics.tabs.campaigns"),
            children: (
              <CampaignsTable campaigns={campaigns} leads={leads} loading={campaignsLoading || campaignsFetching} />
            ),
          },
          {
            key: "leads",
            label: t("marketingAnalytics.tabs.leads"),
            children: <LeadsTable leads={leads} loading={leadsLoading || leadsFetching} />,
          },
          {
            key: "checkout",
            label: t("marketingAnalytics.tabs.checkout"),
            children: <CheckoutFunnelTab from={filters.from} to={filters.to} />,
          },
        ]}
      />
    </Card>
  );
};

export default MarketingAnalytics;
